import { useState, useMemo, useCallback } from "react";

export type SortDirection = "asc" | "desc" | null;

export interface SortState<K extends string = string> {
  key: K | null;
  direction: SortDirection;
}

/**
 * Generic client-side sorting hook.
 * Pass the data array and get back a sorted copy + sort controls.
 */
export function useTableSort<T, K extends string = string>(
  data: T[] | undefined,
  accessors: Partial<Record<K, (item: T) => unknown>>,
) {
  const [sort, setSort] = useState<SortState<K>>({ key: null, direction: null });

  const toggle = useCallback(
    (key: K) => {
      setSort((prev) => {
        if (prev.key !== key) return { key, direction: "asc" };
        if (prev.direction === "asc") return { key, direction: "desc" };
        return { key: null, direction: null };
      });
    },
    [],
  );

  const sorted = useMemo(() => {
    if (!data) return data;
    if (!sort.key || !sort.direction) return data;

    const accessor = accessors[sort.key];
    if (!accessor) return data;

    const dir = sort.direction === "asc" ? 1 : -1;

    return [...data].sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);

      // Nulls last
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;

      if (typeof va === "string" && typeof vb === "string") {
        return va.localeCompare(vb) * dir;
      }

      if (typeof va === "number" && typeof vb === "number") {
        return (va - vb) * dir;
      }

      if (va instanceof Date && vb instanceof Date) {
        return (va.getTime() - vb.getTime()) * dir;
      }

      // Fallback: compare as strings
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [data, sort, accessors]);

  return { sorted, sort, toggle };
}
